import { Modal, ModalProps, withModalConditionalRender } from '@billy/design-system'
import { DEFAULT_FORMATTER_LOCALE } from '@billy/design-system/src/formatters/constants/defaultFormatterLocale'
import { ReactElement, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useSegment } from '../../../../hooks'
import { DepositInvoiceConfig } from '../../types/invoiceDeposit/depositInvoiceConfig'
import { InvoiceLine } from '../../types/invoices/invoiceLine'
import { DepositInvoiceDetailsFormContextProvider } from './contexts/depositInvoiceDetailsFormContext'
import { DepositInvoiceDetailsModalContextProvider } from './contexts/depositInvoiceDetailsModalContext'
import { DepositInvoiceDetailsModalBody } from './elements/DepositInvoiceDetailsModalBody'
import { DepositInvoiceDetailsModalFooter } from './elements/DepositInvoiceDetailsModalFooter'
import { DepositInvoiceDetailsForm } from './types/depositInvoiceDetailsForm'

export interface DepositInvoiceDetailsModalProps extends ModalProps {
  quoteCurrency: string
  quoteLocale?: string
  onClose: () => void
  onConfirm: (params: DepositInvoiceDetailsForm) => void
  predefinedValues?: DepositInvoiceConfig
  quoteAmount: number
  quoteLines: InvoiceLine[]
}

const DepositInvoiceDetailsModalComponent = ({
  quoteCurrency,
  quoteLocale = DEFAULT_FORMATTER_LOCALE,
  onClose,
  onConfirm,
  predefinedValues,
  quoteAmount,
  quoteLines,
  ...rest
}: DepositInvoiceDetailsModalProps): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const isEditMode = useMemo(() => !!predefinedValues, [predefinedValues])

  useEffect(() => {
    track('XXX - Invoicing Core - Deposit Modal - Opened')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const headerTitle = useMemo(() => {
    return isEditMode
      ? t('external_invoices.deposit_invoice_details_modal.title.edit')
      : t('external_invoices.deposit_invoice_details_modal.title.create')
  }, [isEditMode, t])

  return (
    <DepositInvoiceDetailsModalContextProvider
      quoteCurrency={quoteCurrency}
      quoteLines={quoteLines}
      quoteLocale={quoteLocale}
    >
      <DepositInvoiceDetailsFormContextProvider
        onSubmit={onConfirm}
        predefinedValues={predefinedValues}
        quoteAmount={quoteAmount}
      >
        <Modal size="s" {...rest}>
          <Modal.Header title={headerTitle} />
          <Modal.Body>
            <DepositInvoiceDetailsModalBody />
          </Modal.Body>
          <Modal.Footer>
            <DepositInvoiceDetailsModalFooter onClose={onClose} />
          </Modal.Footer>
        </Modal>
      </DepositInvoiceDetailsFormContextProvider>
    </DepositInvoiceDetailsModalContextProvider>
  )
}

export const DepositInvoiceDetailsModal = withModalConditionalRender(DepositInvoiceDetailsModalComponent)
